"use client";

import { useRef, useState } from "react";

import { getInitials, cn } from "@/lib/utils";
import {
	getSavedAccountDisplayName,
	isSavedLoginAccountSignedOut,
} from "@/lib/auth/saved-accounts";
import type { SavedLoginAccount } from "@/lib/auth/saved-accounts";
import AvatarWrapper from "./AvatarWrapper";

const SWIPE_REVEAL_WIDTH = 88;
const SWIPE_COMMIT_THRESHOLD = 56;

type Props = {
	account: SavedLoginAccount;
	isCurrent: boolean;
	isSwitching: boolean;
	disabled: boolean;
	onSelect: () => void;
	onRemove: () => void;
};

export default function AccountSwitcherAccountRow({
	account,
	isCurrent,
	isSwitching,
	disabled,
	onSelect,
	onRemove,
}: Props) {
	const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
	const [swipeOffset, setSwipeOffset] = useState(0);
	const isSignedOut = !isCurrent && isSavedLoginAccountSignedOut(account);
	const canSwipeRemove = !isCurrent;

	const resetSwipe = () => {
		swipeStartRef.current = null;
		setSwipeOffset(0);
	};

	const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
		if (!canSwipeRemove || event.button !== 0) return;
		swipeStartRef.current = { x: event.clientX, y: event.clientY };
	};

	const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
		if (!canSwipeRemove || !swipeStartRef.current) return;

		const deltaX = event.clientX - swipeStartRef.current.x;
		const deltaY = event.clientY - swipeStartRef.current.y;
		if (Math.abs(deltaY) > Math.abs(deltaX)) return;

		setSwipeOffset(Math.max(-SWIPE_REVEAL_WIDTH, Math.min(0, deltaX)));
	};

	const handlePointerEnd = () => {
		if (!canSwipeRemove) return;

		if (Math.abs(swipeOffset) >= SWIPE_COMMIT_THRESHOLD) {
			onRemove();
			resetSwipe();
			return;
		}

		resetSwipe();
	};

	const row = (
		<button
			type="button"
			className="relative z-10 flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-70"
			style={{
				background: "var(--glass-background)",
				transform: swipeOffset ? `translateX(${swipeOffset}px)` : undefined,
				transition: swipeStartRef.current ? "none" : "transform 150ms ease",
			}}
			disabled={disabled}
			onClick={() => {
				if (Math.abs(swipeOffset) > 8) {
					resetSwipe();
					return;
				}
				onSelect();
			}}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerEnd}
			onPointerCancel={handlePointerEnd}
			onPointerLeave={swipeStartRef.current ? handlePointerEnd : undefined}
		>
			<AvatarWrapper
				src={account.avatar}
				alt={`${getSavedAccountDisplayName(account)} avatar`}
				fallback={getInitials(account.firstName, account.lastName)}
				firstName={account.firstName}
				lastName={account.lastName}
				className="flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold dark:text-foreground"
			/>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-semibold text-foreground">
					{getSavedAccountDisplayName(account)}
				</span>
				<span className="block truncate text-xs text-muted-foreground">{account.email}</span>
			</span>
			{isCurrent ? (
				<span className="shrink-0 rounded-full bg-tertiary px-2 py-0.5 text-[10px] font-semibold text-white">
					Current
				</span>
			) : isSwitching ? (
				<span className="shrink-0 text-[10px] font-semibold text-muted-foreground">Switching...</span>
			) : isSignedOut ? (
				<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
					Signed out
				</span>
			) : null}
		</button>
	);

	if (!canSwipeRemove) {
		return <div className="border-t border-tertiary/25">{row}</div>;
	}

	return (
		<div className="relative overflow-hidden border-t border-tertiary/25">
			<div
				className={cn(
					"absolute inset-y-0 right-0 flex items-center justify-center bg-destructive px-4 text-xs font-semibold text-destructive-foreground transition-opacity",
					swipeOffset < 0 ? "opacity-100" : "pointer-events-none opacity-0",
				)}
				style={{ width: SWIPE_REVEAL_WIDTH }}
				aria-hidden={swipeOffset >= 0}
			>
				Remove
			</div>
			{row}
		</div>
	);
}
